/* ──────────────────────────────────────────────────────────────────────────
 * Funding Platform — public page reached from the landing hero "Explore Funding"
 * button (route /funding). Lets visitors explore IPOLabs funding options and
 * submit a lead. Ported from the supplied funding site (index / partners /
 * style). Scoped under `.fnd`. The hero is a simple lead-capture form that
 * posts to /api/contact/lead (lands in Admin Center → Contact Leads) with an
 * explicit consent rider for sales contact.
 * ────────────────────────────────────────────────────────────────────────── */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Seo } from "@/components/Seo";
import {
  TrendingUp, Landmark, Hourglass, CreditCard, Factory, ArrowLeftRight, Check,
} from "lucide-react";
import "./FundingPlatform.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OPTIONS = [
  { icon: TrendingUp, title: "IPO Capital", desc: "Growth and issue-linked capital for companies preparing to list on BSE SME or NSE Emerge." },
  { icon: Landmark, title: "Debt Financing", desc: "Structured debt facilities from banks and NBFCs, sized to your revenue and repayment capacity." },
  { icon: Hourglass, title: "Pre-IPO Term Loans", desc: "Short-to-medium term loans to fund IPO readiness costs — legal, audit, and advisory fees." },
  { icon: CreditCard, title: "Working Capital Facilities", desc: "Cash credit and overdraft lines to keep operations running smoothly through the IPO process." },
  { icon: Factory, title: "SME Funding Solutions", desc: "Purpose-built funding for MSMEs — equipment finance, invoice discounting and term loans." },
  { icon: ArrowLeftRight, title: "Bridge Loans for IPOs", desc: "Short-term capital to cover the gap between IPO filing and listing proceeds." },
];

const PARTNER_GROUPS = [
  { tag: "BANKS", items: [
    { name: "State Bank of India", type: "Term loans & MSME credit", domain: "sbi.co.in" },
    { name: "HDFC Bank", type: "Business & working capital", domain: "hdfcbank.com" },
    { name: "ICICI Bank", type: "Trade finance & invoice discounting", domain: "icicibank.com" },
    { name: "Axis Bank", type: "Unsecured SME loans", domain: "axisbank.com" },
    { name: "Kotak Mahindra Bank", type: "Collateral-free business loans", domain: "kotak.com" },
    { name: "Punjab National Bank", type: "MSME term loans", domain: "pnbindia.in" },
    { name: "Bank of Baroda", type: "SME & working capital", domain: "bankofbaroda.in" },
    { name: "Canara Bank", type: "MSME credit facilities", domain: "canarabank.com" },
    { name: "Union Bank of India", type: "MSME & term loans", domain: "unionbankofindia.co.in" },
    { name: "IDFC FIRST Bank", type: "Business banking & loans", domain: "idfcfirstbank.com" },
    { name: "IndusInd Bank", type: "Working capital finance", domain: "indusind.com" },
    { name: "YES Bank", type: "SME & trade finance", domain: "yesbank.in" },
    { name: "Federal Bank", type: "Business loans", domain: "federalbank.co.in" },
    { name: "IDBI Bank", type: "MSME financing", domain: "idbibank.in" },
    { name: "RBL Bank", type: "Business & working capital", domain: "rblbank.com" },
    { name: "AU Small Finance Bank", type: "MSME & SME loans", domain: "aubank.in" },
  ]},
  { tag: "NBFCs", items: [
    { name: "Bajaj Finance", type: "Collateral-free MSME loans", domain: "bajajfinserv.in" },
    { name: "Tata Capital", type: "SME & business loans", domain: "tatacapital.com" },
    { name: "Aditya Birla Capital", type: "Loan syndication & SME finance", domain: "adityabirlacapital.com" },
    { name: "L&T Finance", type: "Term loans & overdraft", domain: "ltfs.com" },
    { name: "Shriram Finance", type: "Small business lending", domain: "shriramfinance.in" },
    { name: "IIFL Finance", type: "Collateral-free business loans", domain: "iifl.com" },
    { name: "Poonawalla Fincorp", type: "Business & SME loans", domain: "poonawallafincorp.com" },
    { name: "Hero FinCorp", type: "SME & unsecured loans", domain: "herofincorp.com" },
    { name: "Muthoot Finance", type: "Secured business finance", domain: "muthootfinance.com" },
    { name: "Mahindra Finance", type: "SME & enterprise loans", domain: "mahindrafinance.com" },
    { name: "Cholamandalam", type: "SME & working capital", domain: "cholamandalam.com" },
    { name: "SMFG India Credit", type: "Business loans (Fullerton)", domain: "smfgindiacredit.com" },
    { name: "Piramal Finance", type: "MSME & term loans", domain: "piramalfinance.com" },
    { name: "U GRO Capital", type: "MSME-focused lending", domain: "ugrocapital.com" },
    { name: "Edelweiss", type: "Structured & SME finance", domain: "edelweissfin.com" },
  ]},
  { tag: "DIGITAL & UNSECURED LENDERS", items: [
    { name: "Lendingkart", type: "Working-capital loans", domain: "lendingkart.com" },
    { name: "FlexiLoans", type: "GST-based MSME loans", domain: "flexiloans.com" },
    { name: "Indifi", type: "Industry-specific loans", domain: "indifi.com" },
    { name: "NeoGrowth", type: "Merchant cash advances", domain: "neogrowth.in" },
    { name: "Razorpay Capital", type: "Cash-flow-based credit", domain: "razorpay.com" },
    { name: "Axio", type: "Digital unsecured loans", domain: "axio.co.in" },
    { name: "Clix Capital", type: "Unsecured business loans", domain: "clixcapital.com" },
    { name: "Kinara Capital", type: "Collateral-free MSME loans", domain: "kinaracapital.com" },
    { name: "Recur Club", type: "Revenue-based financing", domain: "recurclub.com" },
    { name: "Velocity", type: "Revenue-based funding", domain: "velocity.in" },
    { name: "Klub", type: "Revenue-based financing", domain: "klubworks.com" },
    { name: "GetVantage", type: "Revenue-based financing", domain: "getvantage.co" },
  ]},
];

const partnerInitials = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const TRUST = ["100% confidential", "No obligation", "Pan-India lender network", "Response within 24 hrs"];

const FundingPlatform = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [view, setView] = useState("options"); // "options" | "partners"

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  const amtLabel = amount >= 50 ? "₹50 Cr+" : `₹${amount} Cr`;

  const goApply = () => {
    setView("options");
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Please enter your name."); return; }
    if (!/^[0-9]{10}$/.test(mobile.trim())) { setErr("Please enter a valid 10-digit mobile number."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setErr("Please enter a valid email address."); return; }
    setErr(""); setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact/lead`, {
        full_name: name.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        module: "IPO Funding",
        lead_type: "funding_application",
        query: `Funding amount required: ${amtLabel}. Submitted via the IPOLabs Funding Platform. User consented to sales contact.`,
      });
      setSuccess(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fnd" data-testid="funding-platform-page">
      <Seo
        title="IPOLabs Funding — Capital for Every Stage of Growth"
        description="Explore IPOLabs funding options — IPO capital, debt, working capital and bridge loans. One form matched to the right bank, NBFC or lender for your company."
        path="/funding"
      />

      {/* NAV */}
      <nav className="fnav">
        <div className="fnav-inner">
          <div className="fnav-left">
            <div className="fbrand" onClick={() => navigate("/ipo-navigator")} data-testid="fnd-brand">IPO<span>Navigator</span></div>
            <button className="fnavbtn" onClick={() => navigate("/home")} data-testid="fnd-home" title="Home Page"><i className="ti ti-home" aria-hidden="true" /><span className="fnavbtn-t">Home Page</span></button>
            <button className="fnavbtn oracle" onClick={() => navigate("/oracle")} data-testid="fnd-ask-oracle" title="Ask Oracle {IPO.GPT}"><i className="ti ti-robot" aria-hidden="true" /><span className="fnavbtn-t">Ask Oracle {"{IPO.GPT}"}</span></button>
          </div>
          <ul className="fnav-links">
            <li><button className={view === "options" ? "active" : ""} onClick={() => setView("options")} data-testid="fnd-nav-options">Funding Options</button></li>
            <li><button className={view === "partners" ? "active" : ""} onClick={() => setView("partners")} data-testid="fnd-nav-partners">Funding Partners</button></li>
          </ul>
          <button className="fnav-cta" onClick={goApply} data-testid="fnd-apply-now">Apply Now</button>
        </div>
      </nav>

      <div className="wrap">
        {view === "options" ? (
          <>
            <div className="badge-row">
              <span className="badge">IPO CAPITAL <span className="dot">·</span> DEBT <span className="dot">·</span> WORKING CAPITAL <span className="dot">·</span> BRIDGE LOANS</span>
            </div>

            <div className="page-head">
              <h1>Capital for every stage of your IPO journey</h1>
              <p className="sub">IPOLabs connects your company to a curated network of banks, NBFCs and lenders — one form, matched to the right funding partner for you.</p>
            </div>

            {/* HERO: lead capture form */}
            <form className="form-card" ref={formRef} onSubmit={submit} data-testid="fnd-lead-form">
              {success ? (
                <div className="form-success" data-testid="fnd-form-success">
                  <div className="fs-ic"><Check className="w-6 h-6" strokeWidth={2.5} /></div>
                  <h4>Thank you{name ? `, ${name.split(" ")[0]}` : ""}!</h4>
                  <p>Our funding desk will review your request for {amtLabel} and reach out to you shortly.</p>
                </div>
              ) : (
                <>
                  <div className="field">
                    <label htmlFor="fnd-name">Full Name <span className="req">*</span></label>
                    <input type="text" id="fnd-name" placeholder="Enter your name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="fnd-name" />
                  </div>

                  <div className="field">
                    <label htmlFor="fnd-mobile">Mobile Number <span className="req">*</span></label>
                    <input type="tel" id="fnd-mobile" placeholder="10-digit mobile number" autoComplete="tel" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))} data-testid="fnd-mobile" />
                  </div>

                  <div className="field">
                    <label htmlFor="fnd-email">Email Address <span className="req">*</span></label>
                    <input type="email" id="fnd-email" placeholder="you@company.com" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="fnd-email" />
                  </div>

                  <div className="field">
                    <div className="amount-row">
                      <label htmlFor="fnd-amount" style={{ marginBottom: 0 }}>Funding Amount Required</label>
                      <span className="amount-value" data-testid="fnd-amount-value">{amtLabel}</span>
                    </div>
                    <input type="range" id="fnd-amount" min="0" max="50" step="1" value={amount} onChange={(e) => setAmount(parseInt(e.target.value, 10))} data-testid="fnd-amount" />
                    <div className="range-caps"><span>₹0</span><span>₹50 Cr+</span></div>
                  </div>

                  <button type="submit" className="btn" disabled={submitting} data-testid="fnd-submit">
                    {submitting ? "Submitting…" : "Get Matched With a Funding Partner"}
                  </button>
                  {err && <p className="form-err" data-testid="fnd-form-err">{err}</p>}
                  <p className="form-note" data-testid="fnd-consent-rider">
                    By submitting this form, you agree that IPOLabs and its funding partners may contact you
                    for sales and funding-related purposes. 100% confidential, no obligation.
                  </p>
                </>
              )}
            </form>

            {/* FUNDING OPTIONS */}
            <h2 className="section-title">Funding options, matched to your stage</h2>
            <p className="section-sub">From early-stage working capital to IPO-linked bridge funding — we route every request to the partner best suited to fund it.</p>

            <div className="grid-3">
              {OPTIONS.map((o) => {
                const Icon = o.icon;
                return (
                  <div className="card" key={o.title} data-testid={`fnd-option-${o.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="card-icon"><Icon className="w-5 h-5" strokeWidth={2} /></div>
                    <h3 className="card-title">{o.title}</h3>
                    <p className="card-desc">{o.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* CTA → partners */}
            <div className="cta-strip">
              <h3>Backed by a network of leading banks &amp; NBFCs</h3>
              <p>See the lenders IPOLabs works with to fund companies like yours.</p>
              <button className="btn" onClick={() => setView("partners")} data-testid="fnd-view-partners">View Funding Partners</button>
            </div>
          </>
        ) : (
          <>
            <div className="badge-row">
              <span className="badge">BANKS <span className="dot">·</span> NBFCS <span className="dot">·</span> UNSECURED LENDERS</span>
            </div>

            <div className="page-head">
              <h1>Our funding partners</h1>
              <p className="sub">A vetted network of banks, NBFCs and lenders — ready to fund your growth, IPO readiness, and working capital needs.</p>
            </div>

            <div className="rider" data-testid="fnd-partners-rider">
              <i className="ti ti-info-circle" aria-hidden="true" />
              <p>The bank, NBFC and lender names below are shown <strong>for illustration purposes only</strong>, compiled from general public / web research of India's leading lenders. IPOLabs is not affiliated with, endorsed by, or an official partner of these institutions unless separately confirmed. Your actual funding partners will be identified and discussed with you on a <strong>case-by-case basis</strong>.</p>
            </div>

            {PARTNER_GROUPS.map((g) => (
              <div className="partner-group" key={g.tag}>
                <div className="group-label">
                  <span className="tag">{g.tag}</span>
                  <span className="line" />
                </div>
                <div className="p-grid">
                  {g.items.map((p) => (
                    <div className="partner-card" key={p.name} data-testid={`fnd-partner-${p.domain}`}>
                      <div className="p-logo">
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${p.domain}&sz=128`}
                          alt={p.name}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex"; }}
                        />
                        <span className="p-mono" style={{ display: "none" }}>{partnerInitials(p.name)}</span>
                      </div>
                      <div className="p-tx">
                        <p className="p-name">{p.name}</p>
                        <p className="p-type">{p.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <p className="partner-note">Names shown are illustrative and based on public research — not an endorsement or a confirmed tie-up. Final partners are matched to your profile case-by-case.</p>

            {/* CTA → apply */}
            <div className="cta-strip">
              <h3>Looking for funding?</h3>
              <p>Tell us how much capital you need and we'll match you to the right partner above.</p>
              <button className="btn" onClick={goApply} data-testid="fnd-apply-for-funding">Apply for Funding</button>
            </div>
          </>
        )}
      </div>

      {/* FOOTER */}
      <div className="foot">
        <div className="wrap">
          <div className="trust">
            {TRUST.map((t) => (
              <span className="trust-item" key={t}><Check className="w-3.5 h-3.5" strokeWidth={3} style={{ color: "var(--purple)" }} /> {t}</span>
            ))}
          </div>
          <p className="foot-copy">IPOLabs is a lead-generation platform and does not itself lend or extend credit. All funding is provided directly by our partner banks, NBFCs and lenders, subject to their own eligibility and approval process.</p>
        </div>
      </div>
    </div>
  );
};

export default FundingPlatform;
